import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { artworksAPI } from '../../services/api';
import { artworks as mockArtworks } from '../../data/mock';
import { getImageUrl } from '../../utils/imageUrl';
import { formatPrice, getLowestPrice } from '../../utils/currency';
import { useCurrency } from '../../context/CurrencyContext';
import ProtectedImage from '../common/ProtectedImage';

const FeaturedProducts = () => {
  const [artworks, setArtworks] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const itemsPerPage = 4;
  const scrollRef = useRef(null);

  useEffect(() => {
    const loadArtworks = async () => {
      try {
        const data = await artworksAPI.getAll();
        setArtworks(data.length > 0 ? data : mockArtworks);
      } catch (error) {
        console.error('Error loading artworks:', error);
        setArtworks(mockArtworks);
      } finally {
        setLoading(false);
      }
    };
    loadArtworks();
  }, []);

  const totalPages = Math.ceil(artworks.length / itemsPerPage);

  const visibleArtworks = artworks.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  const nextPage = () => {
    setCurrentPage((prev) => (prev + 1) % totalPages);
  };

  const prevPage = () => {
    setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
  };

  if (loading) {
    return <div className="py-20 text-center text-gray-500">Loading artworks...</div>;
  }

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl md:text-4xl font-light text-center text-gray-900 mb-12" style={{ fontFamily: 'Georgia, serif' }}>
          Featured products
        </h2>

        {/* Products Grid */}
        <div ref={scrollRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {visibleArtworks.map((artwork) => (
            <ProductCard key={artwork.id} artwork={artwork} />
          ))}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center mt-10 space-x-4">
            <button
              onClick={prevPage}
              className="p-2 text-gray-400 hover:text-black transition-colors disabled:opacity-30"
              disabled={currentPage === 0}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-sm text-gray-500">
              {currentPage + 1} / {totalPages}
            </span>
            <button
              onClick={nextPage}
              className="p-2 text-gray-400 hover:text-black transition-colors disabled:opacity-30"
              disabled={currentPage === totalPages - 1}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* View All Button */}
        <div className="text-center mt-8">
          <Link
            to="/collections"
            className="inline-block px-10 py-3 bg-black text-white text-sm tracking-wide hover:bg-gray-800 transition-colors rounded-md"
          >
            View all
          </Link>
        </div>
      </div>
    </section>
  );
};

const ProductCard = ({ artwork }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { currency } = useCurrency();

  const imageUrl = getImageUrl(isHovered && (artwork.hoverImage || artwork.hover_image) 
    ? (artwork.hoverImage || artwork.hover_image) 
    : artwork.image);

  const priceType = artwork.price_type || artwork.priceType || 'from';
  const lowestPrice = getLowestPrice(artwork.sizes, artwork.price);
  const isSold = artwork.sold === true;

  return (
    <Link
      to={`/product/${artwork.slug}`}
      className="group block"
    >
      {/* Image Container */}
      <div className="relative aspect-square overflow-hidden bg-gray-100 rounded-md">
        <ProtectedImage
          src={imageUrl}
          alt={artwork.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        />
      </div>

      {/* Product Info */}
      <div className="mt-4">
        <h3 className="text-sm text-gray-900 group-hover:underline line-clamp-2">
          {artwork.title}
        </h3>
        <p className="mt-2 text-sm text-gray-600">
          {isSold ? (
            <span className="text-red-600 font-medium">Sold</span>
          ) : (
            <>{priceType === 'fixed' ? '' : 'From '}{formatPrice(lowestPrice, currency)}</>
          )}
        </p>
      </div>
    </Link>
  );
};

export default FeaturedProducts;
